import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { type ValidateSignupKeyResponse } from "@/api";
import { useTranslation } from "@/i18n";
import { ROUTES } from "@/lib/constants";
import { AuthSplitLayout } from "./auth-split-layout";
import { RegisterStep1 } from "./register-step-1";
import { RegisterStep2 } from "./register-step-2";
import { RegisterStep3 } from "./register-step-3";

interface WizardState {
  email: string;
  full_name: string;
  signup_key: string;
  workspace_info: ValidateSignupKeyResponse | null;
}

const STEPS = [1, 2, 3] as const;

/**
 * Orchestrates the 3-step register flow. Each step is a self-
 * contained component receiving its own slice of state via props;
 * the wizard owns the cross-step accumulator + navigation.
 */
export function RegisterWizard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<(typeof STEPS)[number]>(1);
  const [state, setState] = useState<WizardState>({
    email: "",
    full_name: "",
    signup_key: "",
    workspace_info: null,
  });

  return (
    <AuthSplitLayout>
      <div className="space-y-8">
        {/* Progress dots — preserved logic, restyled to sit cleanly
            inside the wider split layout. */}
        <div
          role="list"
          aria-label="Register progress"
          className="flex items-center gap-2"
        >
          {STEPS.map((s) => (
            <div
              key={s}
              role="listitem"
              className="flex flex-1 items-center gap-2"
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  step >= s
                    ? "bg-ocean-600 text-white"
                    : "bg-ocean-100 text-ocean-400"
                }`}
                aria-current={step === s ? "step" : undefined}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`h-0.5 flex-1 ${
                    step > s ? "bg-ocean-600" : "bg-ocean-100"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <RegisterStep1
            initialData={{ email: state.email, full_name: state.full_name }}
            onNext={(data) => {
              setState((s) => ({ ...s, ...data }));
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <RegisterStep2
            initialCode={state.signup_key}
            onBack={() => setStep(1)}
            onNext={(data) => {
              setState((s) => ({ ...s, ...data }));
              setStep(3);
            }}
          />
        )}
        {step === 3 && (
          <RegisterStep3
            email={state.email}
            fullName={state.full_name}
            signupKey={state.signup_key}
            workspaceInfo={state.workspace_info}
            onBack={() => setStep(2)}
            onComplete={() => navigate(ROUTES.DASHBOARD, { replace: true })}
          />
        )}

        <p className="text-sm text-neutral-600">
          {t("auth.register.haveAccount")}{" "}
          <Link
            to={ROUTES.LOGIN}
            className="font-medium text-ocean-600 underline-offset-4 hover:text-ocean-800 hover:underline"
          >
            {t("auth.register.loginLink")}
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
